import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useEffect, useRef } from 'react';

// ==========================================
// Overview Analytics Types
// ==========================================
export interface AnalyticsAlignmentOverviewItem {
  category: string;
  average_score: number;
  verdict: string;
}

export interface AnalyticsStrengthItem {
  name: string;
  average_score: number;
  total_candidates?: number;
}

export interface AnalyticsCriticalGapItem {
  name: string;
  gap_percentage: number;
  average_score: number;
}

export interface OverviewAnalyticsData {
  total_candidates: number;
  alignment_overview: AnalyticsAlignmentOverviewItem[];
  strengths: AnalyticsStrengthItem[];
  gaps: AnalyticsCriticalGapItem[];
}

interface OverviewAnalyticsResponse {
  message: string;
  data: OverviewAnalyticsData;
}

// ==========================================
// Basic Expectation Analytics Types
// ==========================================
export interface DistributionItem {
  count: number;
  percentage: number;
}

export interface BasicExpectationDistribution {
  excellent: DistributionItem;
  good: DistributionItem;
  average: DistributionItem;
  low: DistributionItem;
}

export interface SalaryBreakdown {
  below_range: number;
  below_range_percentage: number;
  within_range: number;
  within_range_percentage: number;
  above_range: number;
  above_range_percentage: number;
}

export interface BasicExpectationBreakdownItem {
  field: string;
  label: string;
  expected_value: string | { min: number; max: number; currency: string; term: string };
  match_percentage: number;
  match_count: number;
  mismatch_count: number;
  total: number;
  breakdown?: SalaryBreakdown;
  average_salary_expectation?: number;
}

export interface BasicExpectationAnalyticsData {
  average_score: number;
  total_candidates: number;
  verdict: string;
  distribution: BasicExpectationDistribution;
  breakdown: BasicExpectationBreakdownItem[];
}

interface BasicExpectationAnalyticsResponse {
  message: string;
  data: BasicExpectationAnalyticsData | null;
}

// ==========================================
// Soft Skills Analytics Types
// ==========================================
export interface SoftSkillStrengthItem {
  name: string;
  average_score: number;
  total_candidates?: number;
}

export interface SoftSkillGapItem {
  name: string;
  gap_percentage: number;
  average_score: number;
}

export interface SoftSkillsAnalyticsData {
  average_score: number;
  total_candidates: number;
  verdict: string;
  strengths: SoftSkillStrengthItem[];
  gaps: SoftSkillGapItem[];
  version?: number;
}

interface SoftSkillsAnalyticsResponse {
  message: string;
  data: SoftSkillsAnalyticsData | null;
}

// ==========================================
// Work Style Analytics Types
// ==========================================
export interface WorkStyleItem {
  average_score: number;
  name: string;
}

export interface WorkStyleStrengthGap {
  name: string;
  average_score: number;
}

export interface WorkStyleAnalyticsData {
  average_overall_score: number;
  total_candidates: number;
  verdict: string;
  work_styles: {
    prioritizing: WorkStyleItem;
    visualizing: WorkStyleItem;
    planning: WorkStyleItem;
    coordinating: WorkStyleItem;
  };
  strengths: WorkStyleStrengthGap[];
  gaps: WorkStyleStrengthGap[];
  version: number;
}

interface WorkStyleAnalyticsResponse {
  message: string;
  data: WorkStyleAnalyticsData | null;
}

// ==========================================
// Workplace Qualities Analytics Types
// ==========================================
export interface WorkplaceQualityItem {
  title: string;
  quality_code?: number;
  average_score: number;
  match_rate: number;
  matched_count: number;
  total_count: number;
  is_expected?: boolean;
}

export interface WorkplaceQualityGapItem {
  title: string;
  gap_percentage: number;
  average_score: number;
  is_expected?: boolean;
}

export interface WorkplaceQualitiesAnalyticsData {
  average_score: number;
  total_candidates: number;
  verdict: string;
  strengths: WorkplaceQualityItem[];
  gaps: WorkplaceQualityGapItem[];
  all_qualities: WorkplaceQualityItem[];
  version?: number;
}

interface WorkplaceQualitiesAnalyticsResponse {
  message: string;
  data: WorkplaceQualitiesAnalyticsData | null;
}

// ==========================================
// Work Motivators Analytics Types
// ==========================================
export interface WorkMotivatorItem {
  name: string;
  motivator_code?: number;
  average_score?: number;
  match_rate: number;
  matched_count: number;
  total_count: number;
  is_expected?: boolean;
}

export interface WorkMotivatorGapItem {
  name: string;
  gap_percentage: number;
  average_score: number;
  is_expected?: boolean;
}

export interface WorkMotivatorsAnalyticsData {
  average_score: number;
  total_candidates: number;
  verdict: string;
  strengths: WorkMotivatorItem[];
  gaps: WorkMotivatorGapItem[];
  all_motivators: WorkMotivatorItem[];
  version?: number;
}

interface WorkMotivatorsAnalyticsResponse {
  message: string;
  data: WorkMotivatorsAnalyticsData | null;
}

// ==========================================
// Personality Analytics Types
// ==========================================
export interface PersonalityDistribution {
  personality_code: string;
  personality_type: string;
  count: number;
  percentage: number;
}

export interface PersonalityStrengthItem {
  name: string;
  key: string;
  average_score: number;
}

export interface PersonalityGapItem {
  name: string;
  gap_percentage: number;
  average_score: number;
}

export interface CoreAreaItem {
  name: string;
  key: string;
  average_score: number;
}

export interface PersonalityAnalyticsData {
  average_score: number;
  total_candidates: number;
  verdict: string;
  strengths: PersonalityStrengthItem[];
  gaps: PersonalityGapItem[];
  core_areas: CoreAreaItem[];
  personality_distribution: PersonalityDistribution[];
  version?: number;
}

interface PersonalityAnalyticsResponse {
  message: string;
  data: PersonalityAnalyticsData | null;
}

// ==========================================
// Domain Knowledge Analytics Types
// ==========================================
export interface DomainKnowledgeStrengthItem {
  title: string;
  description?: string;
  average_score: number;
  max_points: number;
  average_percentage: number;
}

export interface DomainKnowledgeGapItem {
  title: string;
  gap_percentage: number;
  average_score: number;
  average_percentage: number;
}

export interface DomainKnowledgeGroupItem {
  title: string;
  description?: string;
  average_score: number;
  max_points: number;
  average_percentage: number;
}

export interface DomainKnowledgeAnalyticsData {
  average_score: number;
  average_percentage: number;
  total_possible_score: number;
  total_candidates: number;
  verdict: string;
  strengths: DomainKnowledgeStrengthItem[];
  gaps: DomainKnowledgeGapItem[];
  group_breakdown: DomainKnowledgeGroupItem[];
  version?: number;
}

interface DomainKnowledgeAnalyticsResponse {
  message: string;
  data: DomainKnowledgeAnalyticsData | null;
}

// ==========================================
// Hooks
// ==========================================

/**
 * Hook for fetching overview analytics data
 */
export const useOverviewAnalytics = (assessmentId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['overviewAnalytics', assessmentId],
    queryFn: async () => {
      if (!assessmentId) {
        throw new Error('Assessment ID is required');
      }

      const response = await apiClient.get<OverviewAnalyticsResponse>(
        `/assessment/analytics/${assessmentId}/overview/`
      );
      return response.data.data;
    },
    enabled: !!assessmentId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch overview analytics';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    overviewAnalytics: data,
    isLoading,
    error,
    refetch,
  };
};

/**
 * Hook for fetching basic expectation analytics data
 */
export const useBasicExpectationAnalytics = (assessmentId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['basicExpectationAnalytics', assessmentId],
    queryFn: async () => {
      if (!assessmentId) {
        throw new Error('Assessment ID is required');
      }

      const response = await apiClient.get<BasicExpectationAnalyticsResponse>(
        `/assessment/analytics/${assessmentId}/basic-expectation/`
      );
      return response.data.data;
    },
    enabled: !!assessmentId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch basic expectation analytics';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    basicExpectationAnalytics: data,
    isLoading,
    error,
    refetch,
  };
};

/**
 * Hook for fetching soft skills analytics data
 */
export const useSoftSkillsAnalytics = (assessmentId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['softSkillsAnalytics', assessmentId],
    queryFn: async () => {
      if (!assessmentId) {
        throw new Error('Assessment ID is required');
      }

      const response = await apiClient.get<SoftSkillsAnalyticsResponse>(
        `/assessment/analytics/${assessmentId}/soft-skills/`
      );
      return response.data.data;
    },
    enabled: !!assessmentId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch soft skills analytics';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    softSkillsAnalytics: data,
    isLoading,
    error,
    refetch,
  };
};

/**
 * Hook for fetching work style analytics data
 */
export const useWorkStyleAnalytics = (assessmentId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['workStyleAnalytics', assessmentId],
    queryFn: async () => {
      if (!assessmentId) {
        throw new Error('Assessment ID is required');
      }

      const response = await apiClient.get<WorkStyleAnalyticsResponse>(
        `/assessment/analytics/${assessmentId}/work-style/`
      );
      return response.data.data;
    },
    enabled: !!assessmentId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch work style analytics';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    workStyleAnalytics: data,
    isLoading,
    error,
    refetch,
  };
};

/**
 * Hook for fetching workplace qualities analytics data
 */
export const useWorkplaceQualitiesAnalytics = (assessmentId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['workplaceQualitiesAnalytics', assessmentId],
    queryFn: async () => {
      if (!assessmentId) {
        throw new Error('Assessment ID is required');
      }

      const response = await apiClient.get<WorkplaceQualitiesAnalyticsResponse>(
        `/assessment/analytics/${assessmentId}/workplace-qualities/`
      );
      return response.data.data;
    },
    enabled: !!assessmentId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch workplace qualities analytics';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    workplaceQualitiesAnalytics: data,
    isLoading,
    error,
    refetch,
  };
};

/**
 * Hook for fetching work motivators analytics data
 */
export const useWorkMotivatorsAnalytics = (assessmentId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['workMotivatorsAnalytics', assessmentId],
    queryFn: async () => {
      if (!assessmentId) {
        throw new Error('Assessment ID is required');
      }

      const response = await apiClient.get<WorkMotivatorsAnalyticsResponse>(
        `/assessment/analytics/${assessmentId}/work-motivators/`
      );
      return response.data.data;
    },
    enabled: !!assessmentId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch work motivators analytics';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    workMotivatorsAnalytics: data,
    isLoading,
    error,
    refetch,
  };
};

/**
 * Hook for fetching personality analytics data
 */
export const usePersonalityAnalytics = (assessmentId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['personalityAnalytics', assessmentId],
    queryFn: async () => {
      if (!assessmentId) {
        throw new Error('Assessment ID is required');
      }

      const response = await apiClient.get<PersonalityAnalyticsResponse>(
        `/assessment/analytics/${assessmentId}/personality/`
      );
      return response.data.data;
    },
    enabled: !!assessmentId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch personality analytics';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
    
    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    personalityAnalytics: data,
    isLoading,
    error,
    refetch,
  };
};

/**
 * Hook for fetching domain knowledge analytics data
 */
export const useDomainKnowledgeAnalytics = (assessmentId?: string) => {
  const { openToast } = useToast();
  const hasShownError = useRef(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['domainKnowledgeAnalytics', assessmentId],
    queryFn: async () => {
      if (!assessmentId) {
        throw new Error('Assessment ID is required');
      }

      const response = await apiClient.get<DomainKnowledgeAnalyticsResponse>(
        `/assessment/analytics/${assessmentId}/domain-knowledge/`
      );
      return response.data.data;
    },
    enabled: !!assessmentId,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error && !hasShownError.current) {
      hasShownError.current = true;
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'Failed to fetch domain knowledge analytics';
      openToast({
        show: true,
        heading: "Error",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }

    if (!error) {
      hasShownError.current = false;
    }
  }, [error, openToast]);

  return {
    domainKnowledgeAnalytics: data,
    isLoading,
    error,
    refetch,
  };
};
